import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import logoMark from "@/assets/logo-mark.png";
import { useSeo } from "@/lib/seo";

export const Route = createFileRoute("/reset-password")({
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  useSeo({
    title: "Set a New Password | GreenTrack",
    description: "Choose a new password for your GreenTrack account and get back to tracking your days.",
    path: "/reset-password",
  });
  const [ready, setReady] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    // Supabase parses the recovery token from the URL hash automatically
    // and fires a PASSWORD_RECOVERY event.
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY" || event === "SIGNED_IN") {
        setReady(true);
      }
    });
    // Also check if we already have a session (link already consumed)
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) setReady(true);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      // GreenTrack is iOS-only. Don't leave the user signed in on the website —
      // sign them out and bounce back to the native app.
      await supabase.auth.signOut();
      setDone(true);
      setTimeout(() => {
        window.location.href = "app.greentrack.life://reset-complete";
      }, 800);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not update password.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen w-full bg-paper text-ink flex items-center justify-center p-6">
      <div className="w-full max-w-sm space-y-8">
        <div className="text-center flex flex-col items-center">
          <img src={logoMark} alt="GreenTrack" className="h-20 w-auto object-contain" />
          <h1 className="mt-6 font-display text-xl font-bold text-ink-deep">
            Set a new password
          </h1>
        </div>

        {done ? (
          <div className="space-y-3">
            <div className="border border-ink-deep/30 bg-paper p-4 text-sm text-ink-deep">
              Password updated. Return to the GreenTrack app to sign in.
            </div>
            <a
              href="app.greentrack.life://reset-complete"
              className="flex w-full items-center justify-center bg-ink-deep px-6 py-4 text-sm font-bold uppercase tracking-widest text-paper"
            >
              Open GreenTrack app
            </a>
          </div>
        ) : !ready ? (
          <p className="text-center text-xs text-ink-muted">
            Verifying reset link… If nothing happens, request a new link from the sign-in
            screen.
          </p>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <label className="block">
              <span className="text-[10px] font-bold uppercase tracking-widest text-ink-muted">
                New password
              </span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                required
                minLength={8}
                className="mt-1 block w-full border border-rule bg-paper px-3 py-3 text-sm text-ink-deep outline-none focus:border-ink-deep"
                placeholder="At least 8 characters"
              />
            </label>
            <label className="block">
              <span className="text-[10px] font-bold uppercase tracking-widest text-ink-muted">
                Confirm password
              </span>
              <input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                required
                minLength={8}
                className="mt-1 block w-full border border-rule bg-paper px-3 py-3 text-sm text-ink-deep outline-none focus:border-ink-deep"
                placeholder="Repeat password"
              />
            </label>
            <button
              type="submit"
              disabled={busy}
              className="flex w-full items-center justify-center gap-2 bg-ink-deep px-6 py-4 text-sm font-bold uppercase tracking-widest text-paper transition-colors hover:bg-ink disabled:opacity-50"
            >
              {busy ? "Updating…" : "Update password"}
            </button>
            {error && (
              <div className="border border-destructive/40 bg-destructive/10 p-3 text-xs text-destructive">
                {error}
              </div>
            )}
          </form>
        )}

        <p className="text-center text-xs text-ink-muted">
          <Link to="/auth" className="underline underline-offset-2">
            Back to sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
